import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { hasRole, useApi, useApp } from "../context/AppContext";
import { SHAPES, SIZE_CLASSES, STATUS_TONE, loadCatalogueContext } from "../lib/catalogue";

// The catalogue (docs/tasks/35, Products): every product with its shape and
// size, its photos, and what the cabinets know of it - its training on each
// cabinet type, the shelves and weight trays it is placed on. Photos and
// training visits are made with the capture app; this is where they are read,
// corrected and followed.

const PHOTO_STATUS = {
  draft: "no photos", uploaded: "photos, not processed", preprocessed: "processed, not validated", validated: "ready",
  failed: "photo check failed", dataset_ready: "ready", training_ready: "ready",
};

function NewProduct() {
  const api = useApi();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ean_code: "", product_name: "" });
  const [err, setErr] = useState(null);
  const submit = async (e) => {
    e.preventDefault(); setErr(null);
    try {
      const p = await api.createProduct({ ean_code: form.ean_code.trim(), product_name: form.product_name.trim() || null });
      navigate(`/products/${p.id}`);
    } catch (ex) { setErr(String(ex.message || ex)); }
  };
  if (!open) return <button className="btn btn-secondary" onClick={() => setOpen(true)}>New product</button>;
  return (
    <form className="flex flex-wrap items-center gap-2" onSubmit={submit}>
      <input className="input w-44" required placeholder="EAN / GTIN" value={form.ean_code} onChange={(e) => setForm({ ...form, ean_code: e.target.value })} />
      <input className="input w-56" placeholder="name (or from the catalogue)" value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })} />
      <button className="btn btn-primary" type="submit">Create</button>
      <button className="btn btn-secondary" type="button" onClick={() => setOpen(false)}>Cancel</button>
      {err && <span className="text-xs text-rose-700">{err}</span>}
    </form>
  );
}

export function ProductListPage() {
  const api = useApi();
  const { user } = useApp();
  const [products, setProducts] = useState(null);
  const [ctx, setCtx] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState({ status: "", shape: "", size: "", training: "" });
  const [err, setErr] = useState(null);
  const mayWrite = hasRole(user, "tenant_admin", "product_manager");

  useEffect(() => {
    api.listProducts().then(setProducts).catch((e) => setErr(String(e.message || e)));
    loadCatalogueContext(api, setCtx).then(setCtx).catch(() => setCtx(null));
  }, [api]);

  const rows = useMemo(() => {
    let out = products || [];
    const needle = q.trim().toLowerCase();
    if (needle) out = out.filter((p) => [p.product_name, p.brand, p.ean_code, p.id].some((v) => String(v || "").toLowerCase().includes(needle)));
    if (filter.status) out = out.filter((p) => p.status === filter.status);
    if (filter.shape) out = out.filter((p) => (p.product_type || "") === (filter.shape === "-" ? "" : filter.shape));
    if (filter.size) out = out.filter((p) => (p.size_class || "") === (filter.size === "-" ? "" : filter.size));
    if (filter.training && ctx) {
      const [typeId, want] = filter.training.split(":");
      out = out.filter((p) => (ctx.statusByType[typeId]?.[p.id]?.status || "untrained") === want);
    }
    return [...out].sort((a, b) => String(a.product_name || a.ean_code).localeCompare(String(b.product_name || b.ean_code)));
  }, [products, q, filter, ctx]);

  const types = ctx?.types || [];
  const set = (k) => (e) => setFilter({ ...filter, [k]: e.target.value });
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Products</h1>
          <p className="text-sm text-slate-500">The catalogue the cabinets recognise: shape, size, photos, and each product's training on every cabinet type and where it is placed.</p>
        </div>
        {mayWrite && <NewProduct />}
      </div>
      <div className="card flex flex-wrap items-center gap-2 text-xs">
        <input className="input w-56 py-1 text-xs" placeholder="search name, brand, EAN" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="input w-auto py-1 text-xs" value={filter.status} onChange={set("status")}>
          <option value="">photos: any</option>
          {Object.entries(PHOTO_STATUS).map(([k, v]) => <option key={k} value={k}>{k} - {v}</option>)}
        </select>
        <select className="input w-auto py-1 text-xs" value={filter.shape} onChange={set("shape")}>
          <option value="">shape: any</option>
          {Object.entries(SHAPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          <option value="-">not set</option>
        </select>
        <select className="input w-auto py-1 text-xs" value={filter.size} onChange={set("size")}>
          <option value="">size: any</option>
          {Object.entries(SIZE_CLASSES).map(([k, [v]]) => <option key={k} value={k}>{v}</option>)}
          <option value="-">not set</option>
        </select>
        <select className="input w-auto py-1 text-xs" value={filter.training} onChange={set("training")}>
          <option value="">training: any</option>
          {types.flatMap((t) => ["trained", "partial", "untrained"].map((s) => <option key={`${t.id}:${s}`} value={`${t.id}:${s}`}>{t.name}: {s}</option>))}
        </select>
        <span className="ml-auto text-slate-500">{rows.length} of {(products || []).length}</span>
      </div>
      {err && <p className="text-sm text-rose-700">{err}</p>}
      <div className="card overflow-x-auto">
        {!products ? <p className="text-sm text-slate-500">Loading…</p> : (
          <table className="table text-xs">
            <thead>
              <tr>
                <th>Product</th><th>EAN</th><th>Shape</th><th>Size</th><th>Photos</th>
                {types.map((t) => <th key={t.id} title={t.name}>Training · {t.name.replace(/\s*\(.*\)$/, "")}</th>)}
                <th>On shelves</th><th>Weight trays</th><th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => {
                const shelves = ctx?.placements[p.id] || [];
                const trays = ctx?.trays[p.id] || [];
                return (
                  <tr key={p.id}>
                    <td>
                      <Link className="font-medium text-sky-700 hover:underline" to={`/products/${p.id}`}>{p.product_name || <span className="text-amber-700">no name</span>}</Link>
                      {p.brand ? <div className="text-slate-500">{p.brand}</div> : null}
                    </td>
                    <td className="font-mono">{p.ean_code}</td>
                    <td>{SHAPES[p.product_type] || <span className="text-amber-700">not set</span>}</td>
                    <td>{SIZE_CLASSES[p.size_class]?.[0] || (p.dimensions_mm ? p.dimensions_mm.map((x) => Math.round(x)).join("×") + " mm" : <span className="text-amber-700">not set</span>)}</td>
                    <td>
                      <span className={p.status === "failed" ? "text-rose-700" : ["validated", "dataset_ready", "training_ready"].includes(p.status) ? "text-emerald-700" : "text-amber-700"}>{PHOTO_STATUS[p.status] || p.status}</span>
                      <div className="text-slate-500">{p.training_image_count} + {p.test_image_count} test{p.last_validation_score != null ? ` · ${Math.round(p.last_validation_score * 100)}%` : ""}</div>
                    </td>
                    {types.map((t) => {
                      const s = ctx?.statusByType[t.id]?.[p.id];
                      const st = s?.status || "untrained";
                      return (
                        <td key={t.id}>
                          <span className={`rounded px-1.5 py-0.5 ${STATUS_TONE[st]}`}>{st}</span>
                          {s ? <div className="text-slate-500">{Object.entries(s.rows_by_camera || {}).map(([c, n]) => `${c}:${n}`).join(" ")}{s.confident === false ? " · weak" : ""}</div> : null}
                        </td>
                      );
                    })}
                    <td>{shelves.length ? shelves.map((x) => `${x.name} #${x.shelf}`).join(", ") : "—"}</td>
                    <td>{trays.length ? trays.map((x) => `${x.name} ${x.tray}`).join(", ") : "—"}</td>
                    <td className="text-slate-500">{p.updated_at ? new Date(p.updated_at).toLocaleDateString() : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
